import { useState } from "react";
import { renderMetaTags, StructuredTextGraphQlResponse, TitleMetaLinkTag, useQuerySubscription } from "react-datocms";
import { GetStaticProps } from "next";
import { Api, ProductName, ProductInterval, IPlanObject, IPlanFeature } from "@/api";
import { Section, Layout, Container, SEO } from "@/components";
import { EXTERNAL, PAGE_RECORDS, PUBLISH_PARKING_PLAN_ID, PUBLISH_TRIAL_PLAN_ID, DATOCMS_API_TOKEN } from "@/config";
import { metaTagsFragment, Modules, modulesField, modulesFragments, ModuleToComponent } from "@/modules";
import { SiteBanner } from "@/modules/site-banner";
import { IPage, Subscription } from "@/types";
import { createUrl } from "@/utils";
import { Pricing } from "@narrative-software/narrative-web-ui";

export interface ICoupon {
  id: string;
  percent_off: number;
}

interface IPricingPage {
  parentPage: IPage;
  modules: Modules;
  seo: TitleMetaLinkTag[];
}

type CmsQueryResult = {
  pages: IPricingPage[];
  banner: {
    enabled: boolean;
    content: StructuredTextGraphQlResponse;
  };
};

type PageContent = {
  page: IPricingPage;

  banner: {
    enabled: boolean;
    content: StructuredTextGraphQlResponse;
  };
};

type Props = {
  plans: (IPlanObject & { attributes: { features: { yearly: IPlanFeature[]; monthly: IPlanFeature[] } } })[];
  preview: boolean;
  subscription: Subscription<PageContent>;
};

const PricingPage: React.FC<Props> = ({ plans, preview, subscription }) => {
  const { data: { page, banner } = {} } = useQuerySubscription(subscription);

  const [interval, setInterval] = useState<ProductInterval>(ProductInterval.Year);
  const handleIntervalChange = (interval: ProductInterval) => setInterval(interval);

  if (!page) return null;
  const { modules, seo } = page;

  const handleClick = async ({ planID, interval }: { planID: string; interval: ProductInterval }) => {
    if (planID === PUBLISH_TRIAL_PLAN_ID) {
      window.location.href = createUrl(EXTERNAL.PUBLISH.GET_STARTED.HREF, { backPath: "/subscriptions" });
    } else {
      window.location.href = `${createUrl(EXTERNAL.SIGN_UP.HREF, {
        planID,
        interval,
        product: "publish",
        backPath: "/checkout",
        // fp_ref is the query param that the account portal accepts for coupon codes
        // ...(IS_BLACK_FRIDAY ? { fp_ref: blackFridayProduct?.stripeCouponCode } : {}),
      })}`;
    }
  };

  return (
    <Layout
      product="publish"
      preview={preview}
      siteBanner={banner ? <SiteBanner {...banner} /> : null}
      className="overflow-hidden"
    >
      <SEO title="Publish plans | Publish">{renderMetaTags(seo)}</SEO>

      <Section isFirstSection className="mb-28">
        <Container>
          <Pricing
            title="Supercharge your website’s content"
            productName={ProductName.Publish}
            onIntervalChange={handleIntervalChange}
          >
            {plans.map(({ id, attributes }) => (
              <Pricing.Item
                key={id}
                id={id}
                darkMode={
                  // IS_BLACK_FRIDAY &&
                  // attributes.slug === PUBLISH_LIVE_PLAN_SLUGS.pro &&
                  // interval === ProductInterval.Year
                  false
                }
                yearlyPrice={attributes["yearly-price"] / 12}
                billingInfo={
                  attributes["yearly-price"] === 0 ? "No credit card needed" : `USD per month, billed ${interval}ly`
                }
                monthlyPrice={attributes["monthly-price"]}
                yearlyDiscount={
                  undefined
                  // IS_BLACK_FRIDAY &&
                  // attributes.slug === PUBLISH_LIVE_PLAN_SLUGS.pro &&
                  // blackFridayProduct?.percentOff &&
                  // interval === ProductInterval.Year
                  //   ? blackFridayProduct.percentOff / 100
                  //   : undefined
                }
                features={attributes.features}
                onClick={handleClick}
                disableSale={attributes["monthly-price"] === 0}
              >
                {attributes.name}
              </Pricing.Item>
            ))}
          </Pricing>
        </Container>
      </Section>
      {/* Nasty to offset the added padding on the first module */}
      <div className="-mt-12 md:-mt-16 lg:-mt-20 xl:-mt-30">
        <ModuleToComponent modules={modules} />
      </div>
    </Layout>
  );
};

/**
 * Static props
 */
export const getStaticProps: GetStaticProps = async ({ preview = false }) => {
  // Get plans
  const response = await Api.getPlans({ "filter[product-name]": ProductName.Publish });

  const plans = response?.data.data
    // Remove parking plan and some other dank plans in there
    .filter(
      ({ id }) =>
        id !== PUBLISH_PARKING_PLAN_ID &&
        id !== "b0c9ac62-fd70-4105-8dd6-16c3080aee6f" &&
        id !== "2db637e8-fa0c-4f0e-9645-a4da83cc9bff",
    )
    .map(({ id, attributes }) => {
      const isTrialPlan = attributes["default-free"];
      const maxPosts = attributes.features.find(({ slug }) => slug === "post-count")?.quantity;
      return {
        id,
        attributes: {
          ...attributes,
          features: {
            yearly: [
              {
                name: "Total number of posts",
                slug: "post-count",
                quantity: maxPosts || "Unlimited",
                "singular-noun": "Published Post",
                "plural-noun": "Published Posts",
                description:
                  "The total number of posts that can be published at once. This does not renew monthly or yearly.",
              },
              {
                name: "Export posts as JPEGs",
                slug: "export-jpg",
                description: "Save your post as individual JPEG files.",
                active: !isTrialPlan,
              },
              {
                name: "Export image collages",
                slug: "export-collage",
                description: "Save sections of your post to publish on social media.",
                active: !isTrialPlan,
              },
              {
                name: "SEO integration",
                slug: "seo-integration",
                description: "Maximize your SEO gains with our easy-to-use SEO tools.",
              },
              {
                name: "Design offline",
                slug: "design-offline",
                description: "Build posts anywhere, any time. Publish works offline.",
              },
              {
                name: "Text editor",
                slug: "text-editor",
                description:
                  "Add text to your blog post that is formatted to match your website’s style when Published.",
              },
              {
                name: "Pinterest and Facebook plugins",
                slug: "social-plugins",
                description: "Help your work get seen by adding Pinterest share buttons and Facebook comments.",
              },
            ],
            monthly: [
              {
                name: "Total number of posts",
                slug: "post-count",
                quantity: maxPosts || "Unlimited",
                "singular-noun": "Published Post",
                "plural-noun": "Published Posts",
                description:
                  "The total number of posts that can be published at once. This does not renew monthly or yearly.",
              },
              {
                name: "Export image collages",
                slug: "export-collage",
                description: "Save sections of your post to publish on social media.",
                active: !isTrialPlan,
              },
              {
                name: "SEO integration",
                slug: "seo-integration",
                description: "Maximize your SEO gains with our easy-to-use SEO tools.",
              },
              {
                name: "Design offline",
                slug: "design-offline",
                description: "Build posts anywhere, any time. Publish works offline.",
              },
              {
                name: "Text editor",
                slug: "text-editor",
                description:
                  "Add text to your blog post that is formatted to match your website’s style when Published.",
              },
              {
                name: "Pinterest and Facebook plugins",
                slug: "social-plugins",
                description: "Help your work get seen by adding Pinterest share buttons and Facebook comments.",
              },
            ],
          },
        },
      };
    });

  // Move trial plan to start of array
  const trialPlanIndex = plans.findIndex(({ attributes }) => attributes["default-free"]);
  if (trialPlanIndex) {
    plans.unshift(plans.splice(trialPlanIndex, 1)[0]);
  }

  // Get DatoCMS content
  const graphqlRequest = {
    preview,
    query: `
      {
        pages: allPagePricings {
          parentPage {
            ... on ${PAGE_RECORDS.publish} {
              slug
            }
          }
          seo: _seoMetaTags {
            ...metaTagsFragment
          }
          ${modulesField}
        }
        banner: moduleSiteBanner {
          enabled
          content {
            value
          }
        }
      }
      ${modulesFragments}
      ${metaTagsFragment}
    `,
  };

  const {
    data: {
      data: { pages, banner },
    },
  } = await Api.datocmsGraphqlRequest<CmsQueryResult>(graphqlRequest);

  const page = pages.find(({ parentPage }) => parentPage.slug === "publish");
  const initialData = { page, banner };

  return {
    props: {
      plans,
      preview,
      subscription: preview
        ? {
            ...graphqlRequest,
            initialData,
            token: DATOCMS_API_TOKEN,
          }
        : {
            initialData,
            enabled: false,
          },
    },
  };
};

export default PricingPage;
